import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useId,
  useRef,
} from "react";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import {
  Bitcoin,
  ChartCandlestick,
  Coins,
  Layers,
  LucideIcon,
  Scale,
} from "lucide-react";
import Checkbox from "common/Checkbox/Checkbox";
import Input from "common/Inputs/Input";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { hexToRGB } from "@shared/utils/misc";
import { FixMeLater } from "types";
import {
  ClientData,
  ConfigurationExchangeInfo,
  LanguageNameSpaces,
} from "@shared/types";
import useSelectSubExchange from "@shared/features/portfolio/createAccount/useSelectSubExchange";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBottomNavigator } from "utils/hooks/misc";
import {
  CREATE_ACCOUNT_ACCOUNT_NAME_ID,
  CREATE_ACCOUNT_SUB_EXCHANGE_CHECKBOX_ID,
} from "testIds.ts";
import {
  Hint,
  SectionLabel,
} from "features/portfolio/CreateAccount/NewLook/styles";

interface NewLookSelectSubExchangeProps {
  primaryExchange?: string;
  isEdit?: boolean;
  account?: ClientData;
  isLoading?: boolean;
}

const getMarketIcon = (subExchange: ConfigurationExchangeInfo): LucideIcon => {
  const name =
    `${subExchange.exchange} ${subExchange.exchange_display}`.toLowerCase();
  if (/coin-|inverse/.test(name)) return Bitcoin;
  if (subExchange.tag === "Futures") return ChartCandlestick;
  if (subExchange.tag === "Margin") return Scale;
  if (subExchange.tag === "Spot") return Coins;
  return Layers;
};

const NewLookSelectSubExchange = ({
  primaryExchange,
  account,
  isEdit,
  isLoading,
}: NewLookSelectSubExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(undefined);
  const accountNameId = useId();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const {
    subExchanges,
    getCheckboxData,
    accountNameInputData,
    accountNameInitialized,
  } = useSelectSubExchange({ primaryExchange, account, isEdit });

  useEffect(() => {
    if (!isBottomNavigator) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [inputRef, isBottomNavigator]);

  // The title names the exchange, so a chip names the market alone
  // ("Bitget Inverse (USD)" → "Inverse (USD)").
  const getMarketName = (name: string) => {
    const prefix = `${primaryExchange ?? ""} `.toLowerCase();
    return primaryExchange && name.toLowerCase().startsWith(prefix)
      ? name.slice(prefix.length)
      : name;
  };

  const markets = (subExchanges ?? []).map((subExchange) => ({
    subExchange,
    data: getCheckboxData(subExchange),
  }));

  const getOption = ({ subExchange, data }: (typeof markets)[number]) => {
    const Icon = getMarketIcon(subExchange);
    return (
      <Option
        key={data.key}
        value={data.value}
        checked={data.checked}
        onChange={data.onChange}
        $checked={data.checked}
        $error={!!data.exchangeError || !!data.accountError}
        data-testid={`${CREATE_ACCOUNT_SUB_EXCHANGE_CHECKBOX_ID}-${subExchange.exchange}`}
      >
        <Icon size={14} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
        {getMarketName(String(data.exchange))}
      </Option>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={
        !isLoading && !accountNameInitialized && !isExternalConnectAccount
      }
      backgroundColor={theme.colors.layoutBgColor}
    >
      <Sections>
        {!isEdit && (
          <section>
            <SectionLabel>
              {t("create_account.new_look.markets_title")}
            </SectionLabel>
            <MarketsHint>
              {t("create_account.new_look.markets_hint")}
            </MarketsHint>
            <Options>{markets.map(getOption)}</Options>
          </section>
        )}
        {accountNameInputData.shouldDisplay && (
          <Field>
            <FieldLabel htmlFor={accountNameId}>
              {accountNameInputData.label}
            </FieldLabel>
            <Input
              id={accountNameId}
              ref={inputRef as FixMeLater}
              value={accountNameInputData.value}
              status={accountNameInputData.error ? "error" : undefined}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                accountNameInputData.onChange(event.target.value)
              }
              data-testid={CREATE_ACCOUNT_ACCOUNT_NAME_ID}
            />
            {accountNameInputData.error && (
              <FieldError role="alert">{accountNameInputData.error}</FieldError>
            )}
          </Field>
        )}
      </Sections>
    </LoadingSpinnerOverlay>
  );
};

export default NewLookSelectSubExchange;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;
/* Markets side by side (compact cards), so the connection form below
   stays on screen. */
/* Markets as toggle chips in one row (two to four fit), so the connection
   form below stays on screen. */
const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  @media (max-width: 560px) {
    gap: 6px;
  }
`;
const Option = styled(Checkbox)<{ $checked?: boolean; $error?: boolean }>`
  &.ant-checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    min-height: 36px;
    padding: 0 14px 0 10px;
    box-sizing: border-box;
    border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
    border-radius: 999px;
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.titleColor};
    font-size: 13.5px;
    font-weight: 500;
    transition: border-color 150ms ease, background-color 150ms ease;
    &:hover {
      border-color: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.45)};
    }
    &::after {
      display: none;
    }
    ${(p) =>
      p.$checked &&
      css`
        border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.55)};
        background: ${hexToRGB(
          p.theme.colors.primaryBlue,
          p.theme.isDarkMode ? 0.1 : 0.05
        )};
      `}
    ${(p) =>
      p.$error &&
      css`
        border-color: ${p.theme.colors.red};
        color: ${p.theme.colors.red};
      `}
    &:has(.ant-checkbox-input:focus-visible) {
      outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
      outline-offset: 2px;
    }
  }
  > span:last-child {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    white-space: nowrap;
    svg {
      color: ${(p) =>
        p.$checked ? p.theme.colors.primaryBlue : p.theme.colors.secondaryText};
    }
  }
  .ant-checkbox-inner {
    width: 16px;
    height: 16px;
    border-radius: 5px;
  } /* Phones: three markets on one row, so no icons and tighter padding. */
  @media (max-width: 560px) {
    &.ant-checkbox-wrapper {
      gap: 6px;
      padding: 0 12px 0 9px;
      font-size: 13px;
    }
    > span:last-child svg {
      display: none;
    }
  }
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const FieldLabel = styled.label`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const FieldError = styled.div`
  color: ${(p) => p.theme.colors.red};
  font-size: 12.5px;
  line-height: 18px;
`;
const MarketsHint = styled(Hint)`
  @media (max-width: 560px) {
    display: none;
  }
`;
